import React from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";
import Header from "./components/Header";
import Footer from "./components/Footer";
import FloatingCalculator from "./components/FloatingCalculator";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import Home from "./pages/Home";
import Products from "./pages/Products";
import Category from "./pages/Category";
import ProductDetail from "./pages/ProductDetail";
import About from "./pages/About";
import Enquiry from "./pages/Enquiry";
import Dealership from "./pages/Dealership";
import CuttingCalculator from "./pages/CuttingCalculator";
import Events from "./pages/Events";
import Careers from "./pages/Careers";
import CaseStudies from "./pages/CaseStudies";
import CaseStudyDetail from "./pages/CaseStudyDetail";
import FestivalBanner from "./components/FestivalBanner";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import ProductsAdmin from "./pages/admin/ProductsAdmin";
import CategoriesAdmin from "./pages/admin/CategoriesAdmin";
import SubcategoriesAdmin from "./pages/admin/SubcategoriesAdmin";
import ClientsAdmin from "./pages/admin/ClientsAdmin";
import BannersAdmin from "./pages/admin/BannersAdmin";
import EventsAdmin from "./pages/admin/EventsAdmin";
import EnquiriesAdmin from "./pages/admin/EnquiriesAdmin";
import DealershipsAdmin from "./pages/admin/DealershipsAdmin";
import UsersAdmin from "./pages/admin/UsersAdmin";
import SettingsAdmin from "./pages/admin/SettingsAdmin";
import ChangePassword from "./pages/admin/ChangePassword";
import ReviewsAdmin from "./pages/admin/ReviewsAdmin";
import FestivalAdmin from "./pages/admin/FestivalAdmin";
import CareersAdmin from "./pages/admin/CareersAdmin";
import CaseStudiesAdmin from "./pages/admin/CaseStudiesAdmin";
import AnalyticsAdmin from "./pages/admin/AnalyticsAdmin";

import TaskLogin from "./pages/tasks/TaskLogin";
import TaskLayout from "./pages/tasks/TaskLayout";
import TaskDashboard from "./pages/tasks/TaskDashboard";
import TaskBoard from "./pages/tasks/TaskBoard";
import Quotations from "./pages/tasks/Quotations";
import Reports from "./pages/tasks/Reports";
import TaskUsers from "./pages/tasks/TaskUsers";
import Firms from "./pages/tasks/Firms";
import TaskSettings from "./pages/tasks/TaskSettings";
import FlowDashboard from "./pages/tasks/FlowDashboard";
import FlowSalesOrders from "./pages/tasks/FlowSalesOrders";
import FlowApprovals from "./pages/tasks/FlowApprovals";
import FlowStore from "./pages/tasks/FlowStore";
import FlowPurchase from "./pages/tasks/FlowPurchase";
import FlowBilling from "./pages/tasks/FlowBilling";
import FlowLogistics from "./pages/tasks/FlowLogistics";
import FlowEmailDiagnostics from "./pages/tasks/FlowEmailDiagnostics";
import FlowHolidays from "./pages/tasks/FlowHolidays";
import FlowMarketing from "./pages/tasks/FlowMarketing";
import FlowBranding from "./pages/tasks/FlowBranding";
import FlowPendencyReport from "./pages/tasks/FlowPendencyReport";
import FlowSalesAnalytics from "./pages/tasks/FlowSalesAnalytics";
import FlowDeliveryMasters from "./pages/tasks/FlowDeliveryMasters";
import FlowStockReport from "./pages/tasks/FlowStockReport";
import FlowStockUpload from "./pages/tasks/FlowStockUpload";
import ErpApp from "./pages/erp/ErpApp";

const AppContent = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isTaskApp = location.pathname.startsWith('/tasks');
  const isErp = location.pathname.startsWith('/erp');

  return (
    <>
      {!isAdmin && !isTaskApp && !isErp && <FestivalBanner />}
      {!isAdmin && !isTaskApp && !isErp && <Header />}
      <main>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/erp/*" element={<ErpApp />} />
          <Route path="/products" element={<Products />} />
          <Route path="/category/:categoryId" element={<Category />} />
          <Route path="/product/:productId" element={<ProductDetail />} />
          <Route path="/calculator" element={<CuttingCalculator />} />
          <Route path="/events" element={<Events />} />
          <Route path="/about" element={<About />} />
          <Route path="/enquiry" element={<Enquiry />} />
          <Route path="/dealership" element={<Dealership />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:studyId" element={<CaseStudyDetail />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="products" element={<ProductsAdmin />} />
            <Route path="categories" element={<CategoriesAdmin />} />
            <Route path="subcategories" element={<SubcategoriesAdmin />} />
            <Route path="clients" element={<ClientsAdmin />} />
            <Route path="banners" element={<BannersAdmin />} />
            <Route path="events" element={<EventsAdmin />} />
            <Route path="enquiries" element={<EnquiriesAdmin />} />
            <Route path="reviews" element={<ReviewsAdmin />} />
            <Route path="dealerships" element={<DealershipsAdmin />} />
            <Route path="users" element={<UsersAdmin />} />
            <Route path="settings" element={<SettingsAdmin />} />
            <Route path="festival" element={<FestivalAdmin />} />
            <Route path="careers" element={<CareersAdmin />} />
            <Route path="case-studies" element={<CaseStudiesAdmin />} />
            <Route path="analytics" element={<AnalyticsAdmin />} />
            <Route path="change-password" element={<ChangePassword />} />
          </Route>

          {/* Task Management App Routes */}
          <Route path="/tasks" element={<TaskLogin />} />
          <Route path="/tasks" element={<TaskLayout />}>
            <Route path="dashboard" element={<TaskDashboard />} />
            <Route path="board" element={<TaskBoard />} />
            <Route path="quotations" element={<Quotations />} />
            <Route path="reports" element={<Reports />} />
            <Route path="users" element={<TaskUsers />} />
            <Route path="firms" element={<Firms />} />
            <Route path="settings" element={<TaskSettings />} />
            <Route path="flow" element={<FlowDashboard />} />
            <Route path="flow/sales-orders" element={<FlowSalesOrders />} />
            <Route path="flow/approvals" element={<FlowApprovals />} />
            <Route path="flow/store" element={<FlowStore />} />
            <Route path="flow/purchase" element={<FlowPurchase />} />
            <Route path="flow/billing" element={<FlowBilling />} />
            <Route path="flow/logistics" element={<FlowLogistics />} />
            <Route path="flow/delivery-persons" element={<FlowDeliveryMasters />} />
            <Route path="flow/stock-report" element={<FlowStockReport />} />
            <Route path="flow/stock-upload" element={<FlowStockUpload />} />
            <Route path="flow/email-diagnostics" element={<FlowEmailDiagnostics />} />
            <Route path="flow/holidays" element={<FlowHolidays />} />
            <Route path="marketing" element={<FlowMarketing />} />
            <Route path="flow/branding" element={<FlowBranding />} />
            <Route path="flow/pendency" element={<FlowPendencyReport />} />
            <Route path="flow/analytics" element={<FlowSalesAnalytics />} />
          </Route>
        </Routes>
      </main>
      {!isAdmin && !isTaskApp && !isErp && <Footer />}
      {!isAdmin && !isTaskApp && !isErp && <FloatingCalculator />}
      {!isAdmin && !isTaskApp && !isErp && <FloatingWhatsApp />}
      <Toaster />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
